import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserPlus, FilePlus2, CalendarPlus, CalendarDays, Users, FolderKanban, BellRing, Bot, MapPin, Phone, Building2 } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { useQuick } from "../components/Layout";
import { Section, StatusBadge, Empty, Loading, RowLink } from "../components/common";
import { label } from "../lib/constants";
import { fmtTime, fmtDate, fmtDay, money, mapsLink, telLink } from "../lib/helpers";

const ApptRow = ({ a, lang, showDate }) => (
  <div className="row-link" data-testid={`dash-appt-${a.id}`}>
    <div className="mono text-sm font-semibold text-[#D9531E] w-20 shrink-0">{showDate ? fmtDay(a.date, lang) : fmtTime(a.time)}</div>
    <div className="flex-1 min-w-0">
      <div className="font-semibold truncate">{a.customer_name}</div>
      <div className="text-xs text-slate-500 truncate">{label(a.type, lang)} · {showDate && fmtTime(a.time)} {a.address}</div>
    </div>
    {a.address && <a href={mapsLink(a.address)} target="_blank" rel="noreferrer" className="btn-ghost btn-sm px-2" aria-label="Directions" data-testid={`dash-appt-directions-${a.id}`}><MapPin className="w-5 h-5" /></a>}
    {a.customer_phone && <a href={telLink(a.customer_phone)} className="btn-ghost btn-sm px-2" aria-label="Call"><Phone className="w-5 h-5" /></a>}
  </div>
);

const ProjRow = ({ p, lang, extra }) => (
  <RowLink to={`/projects/${p.id}`} testid={`dash-project-${p.id}`}>
    <div className="flex-1 min-w-0">
      <div className="font-semibold truncate">{p.name}</div>
      <div className="text-xs text-slate-500 truncate">{p.customer_name} · {extra || p.address}</div>
    </div>
    <StatusBadge status={p.status} />
  </RowLink>
);

function CrewHome({ d }) {
  const { t, lang } = useLang();
  return (
    <div className="grid gap-4">
      <Section title={t("Today's appointments", "Citas de hoy")} count={d.today_appointments.length} testid="crew-today">
        {d.today_appointments.length ? d.today_appointments.map((a) => <ApptRow key={a.id} a={a} lang={lang} />) : <Empty text={t("Nothing scheduled today", "Nada programado hoy")} />}
      </Section>
      <Section title={t("My projects", "Mis proyectos")} count={d.active_projects.length} testid="crew-projects">
        {d.active_projects.length ? d.active_projects.map((p) => <ProjRow key={p.id} p={p} lang={lang} />) : <Empty text={t("No assigned projects", "Sin proyectos asignados")} />}
      </Section>
      <Section title={t("Upcoming", "Próximas")} testid="crew-upcoming">
        {d.upcoming_appointments.length ? d.upcoming_appointments.map((a) => <ApptRow key={a.id} a={a} lang={lang} showDate />) : <Empty text="—" />}
      </Section>
    </div>
  );
}

export default function Dashboard() {
  const { t, lang } = useLang();
  const { user, can } = useAuth();
  const quick = useQuick();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
  }, []);
  const hi = new Date().getHours() < 12 ? t("Good morning", "Buenos días") : t("Hello", "Hola");
  const head = (
    <div className="mb-5 fade-up flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="dashboard-greeting">{hi}, {user.name.split(" ")[0]}</h1>
        <p className="text-slate-500 mt-1">{fmtDate(new Date().toISOString(), lang)}</p>
      </div>
      {user.platform_admin && <button className="btn-dark" onClick={() => nav("/companies")} data-testid="dashboard-companies-btn"><Building2 className="w-5 h-5" /> {t("Companies", "Empresas")}</button>}
    </div>
  );
  if (!d) return <>{head}<Loading /></>;
  if (user.role === "crew") return <>{head}<CrewHome d={d} /></>;
  const actions = [
    ["new-customer", UserPlus, t("New Customer", "Nuevo cliente"), () => quick.openCustomer(), true],
    can("view_prices") && ["new-estimate", FilePlus2, t("New Estimate", "Nuevo presupuesto"), () => nav("/estimates/new"), true],
    ["new-appointment", CalendarPlus, t("New Appointment", "Nueva cita"), () => quick.openAppt(), true],
    ["calendar", CalendarDays, t("Calendar", "Calendario"), () => nav("/calendar")],
    ["customers", Users, t("Customers", "Clientes"), () => nav("/customers")],
    ["projects", FolderKanban, t("Projects", "Proyectos"), () => nav("/projects")],
    ["followups", BellRing, t("Follow-ups", "Seguimientos"), () => nav("/followups")],
    can("ai_assistant") && ["assistant", Bot, t("AI Assistant", "Asistente IA"), () => nav("/assistant")],
  ].filter(Boolean);
  const stats = [
    ["leads", t("Leads this month", "Prospectos del mes"), d.stats.leads_month],
    ["sent", t("Estimates sent", "Presupuestos enviados"), d.stats.estimates_sent_month],
    ["approved", t("Estimates approved", "Presupuestos aprobados"), d.stats.estimates_approved_month],
    ["warranty", t("Active warranties", "Garantías activas"), d.warranty_active],
  ];
  return (
    <>
      {head}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {actions.map(([id, Icon, l, fn, primary]) => (
          <button key={id} onClick={fn} data-testid={`dash-action-${id}`}
            className={`flex items-center gap-3 rounded-2xl p-4 min-h-[72px] text-left font-bold active:scale-[0.98] transition-transform ${primary ? "bg-[#D9531E] text-white hover:bg-[#C24413]" : "bg-white border border-slate-200 hover:border-[#D9531E]"}`}>
            <Icon className={`w-6 h-6 shrink-0 ${primary ? "" : "text-[#D9531E]"}`} /> <span className="leading-tight">{primary ? "+ " : ""}{l}</span>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        {stats.map(([id, l, v]) => (
          <div key={id} className="card p-4" data-testid={`dash-stat-${id}`}>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{l}</div>
            <div className="mono text-3xl font-semibold mt-1">{v}</div>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-4">
        <Section title={t("Today's appointments", "Citas de hoy")} count={d.today_appointments.length} testid="dash-today-appointments" action={<Link to="/calendar" className="text-sm font-semibold text-[#D9531E]">{t("Calendar", "Calendario")}</Link>}>
          {d.today_appointments.length ? d.today_appointments.map((a) => <ApptRow key={a.id} a={a} lang={lang} />) : <Empty text={t("No appointments today", "Sin citas hoy")} />}
        </Section>
        <Section title={t("Follow-ups due today", "Seguimientos para hoy")} count={d.followups_today.length} testid="dash-followups-today" action={<Link to="/followups" className="text-sm font-semibold text-[#D9531E]">{t("Open", "Abrir")}</Link>}>
          {d.followups_today.length ? d.followups_today.slice(0, 6).map((f) => (
            <RowLink key={f.id} to="/followups" testid={`dash-followup-${f.id}`}>
              <div className="flex-1 min-w-0"><div className="font-semibold truncate">{f.customer_name}</div><div className="text-xs text-slate-500">#{f.estimate_number} · {f.step} {t("days", "días")}</div></div>
              {f.estimate_price != null && <span className="mono text-sm">{money(f.estimate_price)}</span>}
            </RowLink>
          )) : <Empty text={t("All caught up", "Todo al día")} />}
        </Section>
        <Section title={t("Estimates waiting for response", "Presupuestos esperando respuesta")} count={d.estimates_waiting.length} testid="dash-estimates-waiting">
          {d.estimates_waiting.length ? d.estimates_waiting.slice(0, 6).map((e) => (
            <RowLink key={e.id} to={`/estimates/${e.id}`} testid={`dash-estimate-${e.id}`}>
              <div className="flex-1 min-w-0"><div className="font-semibold truncate">{e.customer_name}</div><div className="text-xs text-slate-500">#{e.number} · {t("sent", "enviado")} {fmtDate(e.sent_date, lang)}</div></div>
              {e.price != null && <span className="mono text-sm">{money(e.price)}</span>}
            </RowLink>
          )) : <Empty text="—" />}
        </Section>
        <Section title={t("Contracts waiting for signature", "Contratos esperando firma")} count={d.contracts_waiting.length} testid="dash-contracts-waiting">
          {d.contracts_waiting.length ? d.contracts_waiting.slice(0, 6).map((k) => (
            <RowLink key={k.id} to={`/contracts/${k.id}`} testid={`dash-contract-${k.id}`}>
              <div className="flex-1 min-w-0"><div className="font-semibold truncate">{k.customer_name}</div><div className="text-xs text-slate-500">{k.number}</div></div>
              <StatusBadge status={k.status} />
            </RowLink>
          )) : <Empty text="—" />}
        </Section>
        <Section title={t("Projects needing attention", "Proyectos que requieren atención")} count={d.needs_attention.length} testid="dash-needs-attention">
          {d.needs_attention.length ? d.needs_attention.map((p) => <ProjRow key={p.id} p={p} lang={lang} />) : <Empty text={t("Nothing needs attention", "Nada requiere atención")} />}
        </Section>
        <Section title={t("Upcoming jobs (14 days)", "Próximos trabajos (14 días)")} count={d.upcoming_jobs.length} testid="dash-upcoming-jobs">
          {d.upcoming_jobs.length ? d.upcoming_jobs.map((p) => <ProjRow key={p.id} p={p} lang={lang} extra={`${t("Starts", "Inicia")} ${fmtDate(p.start_date, lang)}`} />) : <Empty text="—" />}
        </Section>
        <Section title={t("Active projects", "Proyectos activos")} count={d.active_projects.length} testid="dash-active-projects">
          {d.active_projects.length ? d.active_projects.slice(0, 8).map((p) => <ProjRow key={p.id} p={p} lang={lang} />) : <Empty text="—" />}
        </Section>
        <Section title={t("Recently completed", "Completados recientemente")} count={d.recently_completed.length} testid="dash-recently-completed">
          {d.recently_completed.length ? d.recently_completed.map((p) => <ProjRow key={p.id} p={p} lang={lang} extra={fmtDate(p.completion_date, lang)} />) : <Empty text="—" />}
        </Section>
        <Section title={t("Warranties expiring (60 days)", "Garantías por vencer (60 días)")} count={d.warranty_expiring.length} testid="dash-warranty" action={<Link to="/warranties" className="text-sm font-semibold text-[#D9531E]">{t("All", "Todas")}</Link>}>
          {d.warranty_expiring.length ? d.warranty_expiring.map((p) => <ProjRow key={p.id} p={p} lang={lang} extra={`${p.warranty_days_left} ${t("days left", "días restantes")}`} />) : <Empty text={t("No warranties expiring soon", "Ninguna garantía por vencer")} />}
        </Section>
        <Section title={t("Upcoming appointments", "Próximas citas")} testid="dash-upcoming-appointments">
          {d.upcoming_appointments.length ? d.upcoming_appointments.slice(0, 8).map((a) => <ApptRow key={a.id} a={a} lang={lang} showDate />) : <Empty text="—" />}
        </Section>
      </div>
    </>
  );
}
